import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BASE_URL } from '../../../api/inquiries';
import { fetchWithAuth } from '../../../api/apiClient';
import AiPresetManager from '../AiPresetManager';
import { ACCEPT_AI_REFERENCE, AI_REFERENCE_HINT } from '../../../constants/uploads';

/**
 * AI 보조작가 — 솔루션·사례·제품 공용
 *
 * 2026-09-23 개편. 이전에는 두 구현이 따로 있었다.
 *   솔루션: AiGeneratorModal (모달, 기능 풍부)
 *   사례:   CaseEditor 안의 인라인 섹션 (축소판, URL 1개만, 클립보드를 txt 파일로 우회)
 *
 * ## 무엇을 바꿨나
 * 1. **자료를 한 목록으로 합쳤다.** 파일·URL·붙여넣은 텍스트가 전부 같은 목록의 카드다.
 *    셋 다 "AI에게 줄 자료"라는 점에서 같은데 입력 경로가 셋으로 갈려 있었다.
 * 2. **지시는 한 칸.** 사례는 지시를 PDF/클립보드로만 줄 수 있었다.
 * 3. **결과를 폼에 바로 쓰지 않는다.** 필드별로 [현재 | 제안]을 비교해 고른 것만 반영한다.
 *    이전에는 즉시 덮어써서 SolutionEditor 가 깊이 1짜리 undo 스냅샷과
 *    window.confirm 을 직접 구현해야 했다. 그 둘이 더 이상 필요 없다.
 * 4. **이미지 처리(썸네일 지정·배경제거·리사이즈)를 뺐다.** 글 쓰기와 다른 일이라
 *    편집기의 이미지 영역이 담당한다. 업로드한 이미지는 여전히 '자료'로 쓰이고
 *    AI 가 본문에 삽입할 수 있다.
 */

const STEPS = [
    { id: 1, label: '자료' },
    { id: 2, label: '지시' },
    { id: 3, label: '검토' },
];

let sourceSeq = 0;
const nextId = () => `src-${++sourceSeq}`;

/** HTML 본문을 비교 화면에 보여주기 위한 평문화 */
const toPlain = (value) => {
    if (value === null || value === undefined) return '';
    const s = Array.isArray(value) ? value.join(', ') : String(value);
    return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const AiAssistantModal = ({
    isOpen,
    onClose,
    contentType,
    fields,
    currentValues = {},
    allowedCategories,
    onApply,
    title = 'AI 보조작가',
}) => {
    const [step, setStep] = useState(1);
    const [sources, setSources] = useState([]);
    const [urlDraft, setUrlDraft] = useState('');
    const [textDraft, setTextDraft] = useState('');
    const [instruction, setInstruction] = useState('');

    const [busy, setBusy] = useState('');       // '' | 'upload' | 'generate'
    const [error, setError] = useState('');
    const [draft, setDraft] = useState(null);
    const [checked, setChecked] = useState({});
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef(null);

    const reset = useCallback(() => {
        setStep(1);
        setError('');
        setDraft(null);
        setChecked({});
        // 자료와 지시는 일부러 유지한다 — 결과가 마음에 안 들어 다시 생성할 때
        // 파일을 다시 올리게 만들지 않기 위해서다.
    }, []);

    useEffect(() => {
        if (isOpen) reset();
    }, [isOpen, reset]);

    /** 붙여넣기로 이미지·텍스트를 바로 자료에 담는다. */
    useEffect(() => {
        if (!isOpen || step !== 1) return;
        const onPaste = async (e) => {
            const items = Array.from(e.clipboardData?.items || []);
            const imageItem = items.find((i) => i.type.startsWith('image/'));
            if (imageItem) {
                const blob = imageItem.getAsFile();
                if (blob) await uploadFiles([new File([blob], `clipboard-${Date.now()}.png`, { type: blob.type })]);
                return;
            }
            const text = e.clipboardData?.getData('text/plain');
            if (text && text.trim().length > 40) {
                setSources((prev) => [
                    ...prev,
                    { id: nextId(), kind: 'text', label: `붙여넣은 텍스트 (${text.trim().length}자)`, text: text.trim(), note: '' },
                ]);
            }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, step]);

    // 훅은 조기 반환보다 위에 있어야 한다(rules-of-hooks).
    const checkedCount = useMemo(
        () => fields.filter((f) => checked[f.key]).length,
        [fields, checked],
    );

    if (!isOpen) return null;

    async function uploadFiles(files) {
        if (!files.length) return;
        setBusy('upload');
        setError('');
        try {
            for (const file of files) {
                const body = new FormData();
                body.append('file', file);
                const res = await fetchWithAuth(`${BASE_URL}/api/upload?folder=tmp`, { method: 'POST', body });
                if (!res.ok) {
                    let msg = `${file.name} 업로드에 실패했습니다.`;
                    try { const b = await res.json(); if (b?.message) msg = b.message; } catch { /* 기본 메시지 사용 */ }
                    throw new Error(msg);
                }
                const data = await res.json();
                setSources((prev) => [
                    ...prev,
                    {
                        id: nextId(),
                        kind: 'file',
                        label: file.name + (data.convertedFrom ? ` (${data.convertedFrom.toUpperCase()} → WEBP 변환됨)` : ''),
                        url: data.url,
                        note: '',
                    },
                ]);
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy('');
        }
    }

    const addUrl = () => {
        const url = urlDraft.trim();
        if (!url) return;
        if (!/^https?:\/\//i.test(url)) {
            setError('http:// 또는 https:// 로 시작하는 주소를 입력하세요.');
            return;
        }
        setSources((prev) => [...prev, { id: nextId(), kind: 'url', label: url, url, note: '' }]);
        setUrlDraft('');
        setError('');
    };

    const addText = () => {
        const text = textDraft.trim();
        if (!text) return;
        setSources((prev) => [...prev, { id: nextId(), kind: 'text', label: `직접 입력 (${text.length}자)`, text, note: '' }]);
        setTextDraft('');
    };

    const setNote = (id, note) => setSources((prev) => prev.map((s) => (s.id === id ? { ...s, note } : s)));
    const removeSource = (id) => setSources((prev) => prev.filter((s) => s.id !== id));

    const generate = async () => {
        setBusy('generate');
        setError('');
        try {
            // 자료 종류별로 API 계약에 맞게 나눠 보낸다.
            const fileUrls = sources.filter((s) => s.kind === 'file').map((s) => ({ url: s.url, description: s.note || s.label }));
            const referenceUrls = sources.filter((s) => s.kind === 'url').map((s) => ({ url: s.url, description: s.note || '' }));
            const specs = sources
                .filter((s) => s.kind === 'text')
                .map((s) => (s.note ? `[${s.note}]\n${s.text}` : s.text))
                .join('\n\n---\n\n');

            if (!fileUrls.length && !referenceUrls.length && !specs && !instruction.trim()) {
                throw new Error('자료를 하나 이상 담거나 지시사항을 입력해 주세요.');
            }

            const res = await fetchWithAuth(`${BASE_URL}/api/ai/generate-draft`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: contentType,
                    specs: specs || currentValues.title || '',
                    fileUrls,
                    referenceUrls,
                    userPrompt: instruction.trim(),
                    // 카테고리는 관리자가 정한 고정 목록 안에서만 고르게 한다.
                    // 넘기지 않으면 AI 가 'ESL' 같은 목록 밖 값을 만들어 낸다(실제로 그랬다).
                    allowedCategories,
                }),
            });
            if (!res.ok) throw new Error('생성 요청이 실패했습니다.');
            const data = await res.json();

            const generated = typeof data.content === 'string' ? safeParse(data.content) : (data.content ?? data.data ?? data);
            if (!generated || typeof generated !== 'object') {
                throw new Error('AI 응답을 해석하지 못했습니다. 다시 시도해 주세요.');
            }

            setDraft(generated);
            // 기본값: 값이 있는 필드만 체크
            const init = {};
            fields.forEach((f) => { init[f.key] = generated[f.key] !== undefined && generated[f.key] !== ''; });
            setChecked(init);
            setStep(3);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy('');
        }
    };

    const apply = () => {
        const patch = {};
        fields.forEach((f) => {
            if (checked[f.key] && draft?.[f.key] !== undefined) patch[f.key] = draft[f.key];
        });
        if (!Object.keys(patch).length) {
            setError('반영할 항목을 하나 이상 선택하세요.');
            return;
        }
        onApply(patch);
        onClose();
    };

    const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="w-full max-w-4xl max-h-[88vh] flex flex-col bg-white rounded-2xl shadow-xl">
                {/* 헤더 + 단계 */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                        <button type="button" onClick={onClose} aria-label="닫기" className="w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100">✕</button>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        {STEPS.map((s, i) => (
                            <React.Fragment key={s.id}>
                                <button
                                    type="button"
                                    onClick={() => (s.id < 3 || draft) && setStep(s.id)}
                                    disabled={s.id === 3 && !draft}
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                                        step === s.id ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40'
                                    }`}
                                >
                                    {s.id}. {s.label}
                                </button>
                                {i < STEPS.length - 1 && <span className="text-gray-300 text-xs">—</span>}
                            </React.Fragment>
                        ))}
                        {sources.length > 0 && (
                            <span className="ml-auto text-xs text-gray-500">자료 {sources.length}건</span>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="mx-6 mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 whitespace-pre-line">
                        {error}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {/* ① 자료 */}
                    {step === 1 && (
                        <div className="space-y-5">
                            <div
                                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                                onDragLeave={() => setDragging(false)}
                                onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(Array.from(e.dataTransfer.files || [])); }}
                                className={`rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${dragging ? 'border-primary-500 bg-primary-50/50' : 'border-gray-300 bg-gray-50'}`}
                            >
                                <p className="text-sm font-bold text-gray-700">파일을 끌어다 놓거나 붙여넣기(Ctrl+V) 하세요</p>
                                <p className="text-xs text-gray-500 mt-1">{AI_REFERENCE_HINT}</p>
                                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy === 'upload'} className="mt-3 px-4 py-2 rounded-lg bg-white border border-gray-300 text-sm font-bold text-gray-700 disabled:opacity-50">
                                    {busy === 'upload' ? '올리는 중…' : '파일 선택'}
                                </button>
                                <input ref={fileInputRef} type="file" multiple accept={ACCEPT_AI_REFERENCE} className="hidden" onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                                <div className="flex gap-2">
                                    <input className={inputCls} value={urlDraft} placeholder="참고 웹페이지 주소" onChange={(e) => setUrlDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addUrl())} />
                                    <button type="button" onClick={addUrl} className="shrink-0 px-3 py-2 rounded-lg bg-gray-100 text-sm font-bold text-gray-700">추가</button>
                                </div>
                                <div className="flex gap-2">
                                    <input className={inputCls} value={textDraft} placeholder="텍스트 직접 입력" onChange={(e) => setTextDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addText())} />
                                    <button type="button" onClick={addText} className="shrink-0 px-3 py-2 rounded-lg bg-gray-100 text-sm font-bold text-gray-700">추가</button>
                                </div>
                            </div>

                            {sources.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-6">아직 담은 자료가 없습니다. 자료 없이 지시만으로 생성할 수도 있습니다.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {sources.map((s) => (
                                        <li key={s.id} className="p-3 rounded-xl border border-gray-200 bg-white">
                                            <div className="flex items-start gap-2">
                                                <span className={`shrink-0 mt-0.5 px-2 py-0.5 rounded text-[11px] font-bold ${
                                                    s.kind === 'file' ? 'bg-emerald-100 text-emerald-700' : s.kind === 'url' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {s.kind === 'file' ? '파일' : s.kind === 'url' ? 'URL' : '텍스트'}
                                                </span>
                                                <span className="flex-1 min-w-0 text-sm text-gray-800 break-all">{s.label}</span>
                                                <button type="button" onClick={() => removeSource(s.id)} aria-label="자료 제거" className="shrink-0 w-7 h-7 rounded text-gray-400 hover:text-red-600">✕</button>
                                            </div>
                                            <input
                                                className="mt-2 w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-700 outline-none focus:ring-1 focus:ring-primary-500"
                                                value={s.note}
                                                placeholder="[선택] 이게 어떤 자료인지 한 줄 (예: 하드웨어 상세 스펙표)"
                                                onChange={(e) => setNote(s.id, e.target.value)}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {/* ② 지시 */}
                    {step === 2 && (
                        <div className="space-y-4">
                            {/* AiPresetManager 가 프리셋 선택 + 자유 입력 칸을 함께 제공한다.
                                여기에 textarea 를 하나 더 두면 같은 값에 묶인 입력란이 둘이 되어 혼란스럽다. */}
                            <AiPresetManager type={contentType} value={instruction} onChange={setInstruction} disabled={busy !== ''} />
                            <p className="text-xs text-gray-500">
                                출처가 없는 수치는 생성하지 않도록 기본 지침이 이미 적용돼 있습니다. 그래도 발행 전에 수치는 꼭 확인하세요.
                                {allowedCategories?.length > 0 && ` 카테고리는 ${allowedCategories.join(' · ')} 중에서만 제안합니다.`}
                            </p>
                        </div>
                    )}

                    {/* ③ 검토 */}
                    {step === 3 && draft && (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-600">반영할 항목을 고르세요. 체크한 것만 폼에 들어갑니다.</p>
                            {fields.map((f) => {
                                const proposed = draft[f.key];
                                const has = proposed !== undefined && proposed !== '' && proposed !== null;
                                const cur = toPlain(currentValues[f.key]);
                                return (
                                    <div key={f.key} className={`rounded-xl border p-3 ${checked[f.key] ? 'border-primary-300 bg-primary-50/30' : 'border-gray-200'}`}>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" disabled={!has} checked={!!checked[f.key]} onChange={(e) => setChecked((p) => ({ ...p, [f.key]: e.target.checked }))} />
                                            <span className="text-sm font-bold text-gray-900">{f.label}</span>
                                            {!has && <span className="text-xs text-gray-400">(제안 없음)</span>}
                                        </label>
                                        {has && (
                                            <div className="grid gap-2 mt-2 md:grid-cols-2">
                                                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
                                                    <p className="text-[11px] font-bold text-gray-500 mb-1">현재</p>
                                                    <p className="text-xs text-gray-600 line-clamp-6 whitespace-pre-line">{cur || '(비어 있음)'}</p>
                                                </div>
                                                <div className="p-2.5 rounded-lg bg-white border border-primary-200">
                                                    <p className="text-[11px] font-bold text-primary-600 mb-1">AI 제안</p>
                                                    <p className="text-xs text-gray-800 line-clamp-6 whitespace-pre-line">{toPlain(proposed)}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* 푸터 */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                    <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-sm font-bold text-gray-700 disabled:opacity-40">이전</button>
                    <div className="flex gap-2">
                        {step === 1 && <button type="button" onClick={() => setStep(2)} className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold">다음: 지시</button>}
                        {step === 2 && (
                            <button type="button" onClick={generate} disabled={busy !== ''} className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold disabled:opacity-50">
                                {busy === 'generate' ? '작성 중…' : '초안 생성'}
                            </button>
                        )}
                        {step === 3 && (
                            <>
                                <button type="button" onClick={() => setStep(2)} className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-sm font-bold text-gray-700">다시 생성</button>
                                <button type="button" onClick={apply} className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold">선택 반영 ({checkedCount})</button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/** AI 응답이 문자열로 올 때의 JSON 파싱. 코드펜스가 섞여 오는 경우를 흡수한다. */
function safeParse(raw) {
    const cleaned = raw.replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
    try {
        return JSON.parse(cleaned);
    } catch {
        // JSON 이 아니면 본문 하나짜리 응답으로 간주한다(구형 프롬프트 호환).
        return { content: raw };
    }
}

export default AiAssistantModal;
